import React, { useState, useEffect } from "react";
import { Table, Card, Tabs, Button, InputNumber, message, Select, Alert } from "antd";
import axios from "axios";

const { Option } = Select;

const NormalDistribution = ({
  fileId,
  fileName,
  setSelectedFile,
  initialConfig,
}) => {
  const [activeTab, setActiveTab] = useState("input");
  const [inputData, setInputData] = useState({
    mean: 0,
    std_dev: 1,
    compute_mode: "probability", // "probability" or "cutoff"
    tails_mode: "one", // "one" or "two"
    cutoff: 1.96, // used if mode: probability, tails: one
    left_cutoff: -1.96, // used if mode: probability, tails: two
    right_cutoff: 1.96,
    probability: 0.95 // used if mode: cutoff
  });

  const [results, setResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  const getAuthToken = () => localStorage.getItem("access_token");

  // Load existing file from DB
  useEffect(() => {
    if (fileId) {
      loadData(fileId);
    } else {
      setInputData({
        mean: 0,
        std_dev: 1,
        compute_mode: "probability",
        tails_mode: "one",
        cutoff: 1.96,
        left_cutoff: -1.96,
        right_cutoff: 1.96,
        probability: 0.95,
        ...initialConfig,
      });
      setResults(null);
      setIsSaved(false);
      setActiveTab("input");
    }
  }, [fileId, initialConfig]);

  const loadData = async (id) => {
    try {
      const token = getAuthToken();
      if (!token) return message.error("Not authenticated.");
      setIsLoading(true);

      const res = await axios.get(
        `/api/retrieve_normal_distribution/${id}/`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setInputData(res.data.input_data);
      setResults(res.data.output_data);
      setIsSaved(true);
      setActiveTab("output");
    } catch (err) {
      console.error("Load error:", err);
      message.error("Failed to load data");
    } finally {
      setIsLoading(false);
    }
  };

  // Save / Update API
  const saveData = async () => {
    try {
      const token = getAuthToken();
      if (!token) return message.error("Not authenticated.");

      if (inputData.std_dev <= 0) {
        return message.error("Standard deviation must be strictly greater than 0");
      }

      setIsLoading(true);

      const payload = {
        name: fileName || "Normal Distribution",
        input_data: {
          mean: inputData.mean,
          std_dev: inputData.std_dev,
          compute_mode: inputData.compute_mode,
          tails_mode: inputData.tails_mode,
        },
      };

      if (inputData.compute_mode === "cutoff") {
        payload.input_data.probability = inputData.probability;
      } else if (inputData.compute_mode === "probability") {
        if (inputData.tails_mode === "one") {
          payload.input_data.cutoff = inputData.cutoff;
        } else {
          payload.input_data.left_cutoff = inputData.left_cutoff;
          payload.input_data.right_cutoff = inputData.right_cutoff;
        }
      }

      console.log("Sending payload:", JSON.stringify(payload, null, 2));

      let res;
      const config = { headers: { Authorization: `Bearer ${token}` } };

      if (fileId) {
        res = await axios.put(
          `/api/update_normal_distribution/${fileId}/`,
          payload,
          config
        );
      } else {
        res = await axios.post(
          `/api/save_normal_distribution/`,
          payload,
          config
        );
      }

      setResults(res.data.output_data);
      if (!fileId && setSelectedFile) {
        setSelectedFile({
          id: res.data.id,
          name: res.data.name,
          type: "normal_distribution",
          inputData: res.data.input_data
        });
      }
      setIsSaved(true);
      setActiveTab("output");
      message.success(fileId ? "Updated successfully" : "Saved successfully");
    } catch (err) {
      console.error("Save error:", err.response?.data || err.message);
      message.error(`Failed to save: ${err.response?.data?.detail || err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setInputData((prev) => ({ ...prev, [field]: value || 0 }));
  };

  // ================== INPUT TABLE ==================
  const renderInputTable = () => {
    if (!inputData) return null;

    return (
      <Card title="Input Data">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', maxWidth: '600px' }}>
          <div>
            <b>Mean (μ):</b>
            <br />
            <InputNumber
              value={inputData.mean}
              style={{ width: '100%', marginTop: 8 }}
              onChange={(val) => handleInputChange("mean", val)}
            />
          </div>
          <div>
            <b>Std Dev (σ):</b>
            <br />
            <InputNumber
              min={0.0001}
              value={inputData.std_dev}
              style={{ width: '100%', marginTop: 8 }}
              onChange={(val) => handleInputChange("std_dev", val)}
            />
          </div>
          <div>
            <b>Compute Mode:</b>
            <br />
            <Select
              value={inputData.compute_mode}
              style={{ width: '100%', marginTop: 8 }}
              onChange={(value) => handleInputChange("compute_mode", value)}
            >
              <Option value="probability">Probability</Option>
              <Option value="cutoff">Cutoff Point (X)</Option>
            </Select>
          </div>
          <div>
            <b>Tails:</b>
            <br />
            <Select
              value={inputData.tails_mode}
              style={{ width: '100%', marginTop: 8 }}
              onChange={(value) => handleInputChange("tails_mode", value)}
            >
              <Option value="one">One Tail</Option>
              <Option value="two">Two Tails</Option>
            </Select>
          </div>

          {inputData.compute_mode === "cutoff" && (
            <div style={{ gridColumn: '1 / -1' }}>
              <b>Probability:</b>
              <br />
              <InputNumber
                min={0}
                max={1}
                step={0.01}
                value={inputData.probability}
                style={{ width: '100%', marginTop: 8 }}
                onChange={(val) => handleInputChange("probability", val)}
              />
            </div>
          )}

          {inputData.compute_mode === "probability" && inputData.tails_mode === "one" && (
            <div style={{ gridColumn: '1 / -1' }}>
              <b>Cutoff (x):</b>
              <br />
              <InputNumber
                value={inputData.cutoff}
                style={{ width: '100%', marginTop: 8 }}
                onChange={(val) => handleInputChange("cutoff", val)}
              />
            </div>
          )}

          {inputData.compute_mode === "probability" && inputData.tails_mode === "two" && (
            <>
              <div>
                <b>Left Cutoff (x1):</b>
                <br />
                <InputNumber
                  value={inputData.left_cutoff}
                  style={{ width: '100%', marginTop: 8 }}
                  onChange={(val) => handleInputChange("left_cutoff", val)}
                />
              </div>
              <div>
                <b>Right Cutoff (x2):</b>
                <br />
                <InputNumber
                  value={inputData.right_cutoff}
                  style={{ width: '100%', marginTop: 8 }}
                  onChange={(val) => handleInputChange("right_cutoff", val)}
                />
              </div>
            </>
          )}

        </div>

        <Button
          type="primary"
          onClick={saveData}
          loading={isLoading}
          style={{ marginTop: 24 }}
        >
          {isSaved ? "Update Analysis" : "Save & Calculate"}
        </Button>
      </Card>
    );
  };

  // ================== OUTPUT TABLE ==================
  const renderOutputTable = () => {
    if (!results) {
      return (
        <Alert
          message="Results not calculated"
          description="Please enter the inputs and press 'Calculate' to generate results."
          type="info"
          showIcon
        />
      );
    }

    if (Object.keys(results).length === 0) return <p>No output data available.</p>;

    return (
      <Card title="Output Results">
        <Card title="Calculated Values" style={{ marginBottom: 16 }}>
          {Object.entries(results).map(([key, val]) => (
            <p key={key}>
              <b>{key}:</b> {val}
            </p>
          ))}
        </Card>
      </Card>
    );
  };

  return (
    <Card title={fileName || "Normal Distribution"}>
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: "input",
            label: "Input Parameters",
            children: renderInputTable()
          },
          {
            key: "output",
            label: "Analysis Results",
            disabled: !results,
            children: renderOutputTable()
          }
        ]}
      />
    </Card>
  );
};

export default NormalDistribution;
